// Curriculum "play next" sequencer — a pure, deterministic layer over the skill
// taxonomy (Part A) and the catalog (Part B/C). Reads which entries the Coach has
// mastered; owns no state, no clock, no RNG (a seeded seam for variety is a
// future option). Apply-preferred within the ability window; teaches to unlock the
// next batch; unstick fallback.

import type { CatalogEntry } from "../songs/catalog";
import { buildMastery, curriculumState } from "../songs/curriculum-state";
import { CURRICULUM_BAND_MAX, SKILLS } from "../songs/skills";
import type { SkillId } from "../types";

type SuggestionStep = "teach" | "apply";

interface Suggestion {
  entry: CatalogEntry;
  /** The skill this suggestion serves. */
  skill: SkillId;
  step: SuggestionStep;
}

interface SequencerInput {
  catalog: readonly CatalogEntry[];
  /** Entry ids the Coach reports fully mastered. */
  masteredIds: ReadonlySet<string>;
  /** RCM grade ceiling for suggestions (default CURRICULUM_BAND_MAX). */
  bandMax?: number;
}

export function nextSuggestion(input: SequencerInput): Suggestion | null {
  const bandMax = input.bandMax ?? CURRICULUM_BAND_MAX;
  const inBand = (e: CatalogEntry): boolean => e.level === undefined || e.level <= bandMax;
  const drills = input.catalog.filter((e) => (e.teaches?.length ?? 0) > 0);

  const { masteredAtoms: mastered, satisfied } = buildMastery(input.catalog, input.masteredIds);
  const hasDrill = (id: SkillId): boolean => drills.some((d) => (d.teaches ?? []).includes(id));

  // APPLY first (the priority flip, §6): play the lowest-difficulty unlocked, in-band
  // piece if one exists, so real music interleaves rather than waiting for every teachable
  // skill to drain. The two-gate unlock (atoms satisfied + difficulty ≤ proficiency +
  // REACH) lives in curriculumState — one source of truth, shared with the discovery map.
  const state = curriculumState(input.catalog, input.masteredIds);
  const unlocked = state.songs
    .filter((s) => s.unlocked && inBand(s.entry))
    .sort(
      (a, b) =>
        (a.entry.tagConfidence === "high" ? 0 : 1) - (b.entry.tagConfidence === "high" ? 0 : 1) ||
        a.difficulty - b.difficulty ||
        a.entry.file.localeCompare(b.entry.file),
    )[0];
  if (unlocked) {
    const uses = unlocked.entry.uses ?? [];
    const skill = uses.find((u) => mastered.has(u)) ?? uses[0] ?? "";
    return { entry: unlocked.entry, skill, step: "apply" };
  }

  // TEACH to unlock: earliest eligible unmastered drilled skill (taxonomy order) — the
  // "learn the next thing to progress" step. A freshly-introduced skill's first encounter
  // is its drill (block-first, one drill); the pieces it unlocks play next (then interleave).
  for (const sk of SKILLS) {
    if (mastered.has(sk.id) || !hasDrill(sk.id)) continue;
    if (!sk.prereqs.every(satisfied)) continue;
    const drill = drills
      .filter((d) => (d.teaches ?? []).includes(sk.id) && inBand(d) && !input.masteredIds.has(d.id))
      .sort((a, b) => (a.level ?? 99) - (b.level ?? 99) || a.file.localeCompare(b.file))[0];
    if (drill) return { entry: drill, skill: sk.id, step: "teach" };
  }

  // UNSTICK (unchanged): skills exhausted and nothing unlocked — route to the drill for an
  // instance atom (chord/scale) blocking the easiest in-band locked song.
  const blocked = state.songs
    .filter((s) => !s.unlocked && inBand(s.entry) && s.lockReason?.kind === "atom")
    .sort((a, b) => a.difficulty - b.difficulty || a.entry.file.localeCompare(b.entry.file));
  for (const s of blocked) {
    const missing = s.lockReason?.kind === "atom" ? s.lockReason.missing : [];
    for (const atom of missing) {
      const drill = drills
        .filter(
          (d) => (d.teaches ?? []).includes(atom) && inBand(d) && !input.masteredIds.has(d.id),
        )
        .sort((a, b) => (a.level ?? 99) - (b.level ?? 99) || a.file.localeCompare(b.file))[0];
      if (drill) return { entry: drill, skill: atom, step: "teach" };
    }
  }
  return null;
}
